import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Search, Percent, Clock, Tag, ShoppingCart, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';

interface MediaOwnerDealsProps {
  ownerId: string;
}

// Deals data using real billboard IDs from the database
const mockDeals = [
  {
    id: 1,
    title: "Summer Special - NYC Digital Premier",
    location: "Times Square, New York NY",
    originalPrice: 1500000,
    discountedPrice: 1125000,
    discountPercent: 25,
    type: "Digital",
    size: "6m x 3m",
    impressions: 125000,
    duration: "15 seconds",
    image: "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=400&h=300&fit=crop",
    dealType: "Limited Time",
    expiresIn: "5 days",
    saved: 375000,
    features: ["LED Display", "Prime Location", "High Traffic"]
  },
  {
    id: 45,
    title: "Bundle Deal - Sydney CBD Premium",
    location: "Sydney CBD, NSW", 
    originalPrice: 1200000,
    discountedPrice: 900000,
    discountPercent: 25,
    type: "Digital",
    size: "Various",
    impressions: 280000,
    duration: "Mixed",
    image: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=400&h=300&fit=crop",
    dealType: "Bundle Offer",
    expiresIn: "12 days",
    saved: 300000,
    features: ["3 Locations", "City Center", "Long Term"]
  },
  {
    id: 46,
    title: "Flash Sale - Melbourne Collins Street",
    location: "Collins Street, Melbourne VIC",
    originalPrice: 980000,
    discountedPrice: 686000,
    discountPercent: 30,
    type: "Digital",
    size: "2m x 1m",
    impressions: 75000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
    dealType: "Flash Sale",
    expiresIn: "2 days",
    saved: 294000,
    features: ["CBD Location", "High Dwell Time", "Premium Display"]
  },
  {
    id: 47,
    title: "Early Bird - Brisbane Transport Hub",
    location: "Brisbane, QLD",
    originalPrice: 850000,
    discountedPrice: 680000,
    discountPercent: 20,
    type: "Transit",
    size: "12m x 3m",
    impressions: 150000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop",
    dealType: "Early Bird",
    expiresIn: "8 days",
    saved: 170000,
    features: ["Transport Hub", "24/7 Visibility", "Large Format"]
  },
  {
    id: 48,
    title: "Clearance - Adelaide Premium",
    location: "Adelaide, SA",
    originalPrice: 600000,
    discountedPrice: 360000,
    discountPercent: 40,
    type: "Digital",
    size: "4m x 2m",
    impressions: 95000,
    duration: "10 seconds",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=400&h=300&fit=crop",
    dealType: "Clearance",
    expiresIn: "1 day",
    saved: 240000,
    features: ["Premium Location", "High Footfall", "Interactive"]
  },
  {
    id: 49,
    title: "Weekend Special - Perth Digital Hub",
    location: "Perth, WA",
    originalPrice: 720000,
    discountedPrice: 576000,
    discountPercent: 20,
    type: "Digital",
    size: "8m x 3m",
    impressions: 45000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1577495508048-b635879837f1?w=400&h=300&fit=crop",
    dealType: "Weekend Special",
    expiresIn: "3 days",
    saved: 144000,
    features: ["Premium Location", "Business Travelers", "Digital Display"]
  },
  {
    id: 50,
    title: "Black Friday Mega Deal - Darwin Digital",
    location: "Darwin, NT",
    originalPrice: 450000,
    discountedPrice: 225000,
    discountPercent: 50,
    type: "Digital",
    size: "5m x 3m",
    impressions: 85000,
    duration: "20 seconds",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
    dealType: "Black Friday",
    expiresIn: "6 hours",
    saved: 225000,
    features: ["City Center", "High Visibility", "Extended Duration"]
  },
  {
    id: 108,
    title: "New Year Promotion - Gold Coast",
    location: "Surfers Paradise, Gold Coast QLD",
    originalPrice: 12000,
    discountedPrice: 8400,
    discountPercent: 30,
    type: "Package",
    size: "Multiple",
    impressions: 200000,
    duration: "Various",
    image: "https://images.unsplash.com/photo-1514395462725-fb4566210144?w=400&h=300&fit=crop",
    dealType: "New Year Special",
    expiresIn: "15 days",
    saved: 3600,
    features: ["Tourist Area", "Beach Front", "Holiday Season"]
  },
  {
    id: 109,
    title: "Student Discount - Canberra University",
    location: "University Avenue, Canberra ACT",
    originalPrice: 3500,
    discountedPrice: 2450,
    discountPercent: 30,
    type: "Classic",
    size: "6m x 3m",
    impressions: 65000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
    dealType: "Student Special",
    expiresIn: "20 days",
    saved: 1050,
    features: ["University Campus", "Young Demographics", "Academic Area"]
  },
  {
    id: 110,
    title: "Corporate Bundle - Newcastle Business",
    location: "Hunter Street, Newcastle NSW",
    originalPrice: 9500,
    discountedPrice: 6650,
    discountPercent: 30,
    type: "Digital",
    size: "8m x 4m",
    impressions: 140000,
    duration: "12 seconds",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop",
    dealType: "Corporate Deal",
    expiresIn: "10 days",
    saved: 2850,
    features: ["Business District", "Professional Audience", "High Impact"]
  },
  {
    id: 111,
    title: "Holiday Special - Cairns Tourism",
    location: "Esplanade, Cairns QLD",
    originalPrice: 7800,
    discountedPrice: 5460,
    discountPercent: 30,
    type: "Transit",
    size: "4m x 2m",
    impressions: 110000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
    dealType: "Holiday Special",
    expiresIn: "7 days",
    saved: 2340,
    features: ["Tourist Hotspot", "Waterfront", "International Visitors"]
  },
  {
    id: 112,
    title: "Sports Event Package - Adelaide Oval",
    location: "North Adelaide, Adelaide SA",
    originalPrice: 15000,
    discountedPrice: 10500,
    discountPercent: 30,
    type: "Package",
    size: "Large Format",
    impressions: 350000,
    duration: "Event Duration",
    image: "https://images.unsplash.com/photo-1577495508048-b635879837f1?w=400&h=300&fit=crop",
    dealType: "Sports Special",
    expiresIn: "4 days",
    saved: 4500,
    features: ["Sports Venue", "Event Marketing", "High Engagement"]
  },
  {
    id: 113,
    title: "Tech Conference Deal - Melbourne CBD",
    location: "Flinders Lane, Melbourne VIC",
    originalPrice: 11000,
    discountedPrice: 7700,
    discountPercent: 30,
    type: "Digital",
    size: "6m x 4m",
    impressions: 180000,
    duration: "15 seconds",
    image: "https://images.unsplash.com/photo-1514395462725-fb4566210144?w=400&h=300&fit=crop",
    dealType: "Conference Special",
    expiresIn: "9 days",
    saved: 3300,
    features: ["Tech Hub", "Innovation District", "Professional Network"]
  },
  {
    id: 114,
    title: "Retail Boost - Perth Shopping Center",
    location: "Murray Street Mall, Perth WA",
    originalPrice: 6800,
    discountedPrice: 4760,
    discountPercent: 30,
    type: "Classic",
    size: "5m x 3m",
    impressions: 120000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop",
    dealType: "Retail Special",
    expiresIn: "14 days",
    saved: 2040,
    features: ["Shopping Center", "Retail Audience", "High Footfall"]
  },
  {
    id: 115,
    title: "Airport Express - Brisbane Domestic",
    location: "Brisbane Airport, Brisbane QLD",
    originalPrice: 13500,
    discountedPrice: 9450,
    discountPercent: 30,
    type: "Transit",
    size: "Multiple Formats",
    impressions: 250000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
    dealType: "Airport Special",
    expiresIn: "11 days",
    saved: 4050,
    features: ["Airport Terminal", "Business Travelers", "Dwell Time"]
  },
  {
    id: 116,
    title: "Festival Season - Fremantle Arts",
    location: "High Street, Fremantle WA",
    originalPrice: 5200,
    discountedPrice: 3640,
    discountPercent: 30,
    type: "Digital",
    size: "4m x 3m",
    impressions: 90000,
    duration: "18 seconds",
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=400&h=300&fit=crop",
    dealType: "Festival Special",
    expiresIn: "18 days",
    saved: 1560,
    features: ["Arts District", "Cultural Events", "Creative Audience"]
  },
  {
    id: 117,
    title: "Highway Mega Deal - Sydney-Melbourne",
    location: "Hume Highway, Multiple Locations",
    originalPrice: 20000,
    discountedPrice: 14000,
    discountPercent: 30,
    type: "Package",
    size: "Large Format",
    impressions: 400000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=400&h=300&fit=crop",
    dealType: "Highway Package",
    expiresIn: "6 days",
    saved: 6000,
    features: ["Interstate Highway", "Long Distance", "High Reach"]
  },
  {
    id: 118,
    title: "Startup Special - Sydney Tech Park",
    location: "Technology Park, Sydney NSW",
    originalPrice: 4200,
    discountedPrice: 2940,
    discountPercent: 30,
    type: "Classic",
    size: "4m x 2m",
    impressions: 55000,
    duration: "Static",
    image: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=400&h=300&fit=crop",
    dealType: "Startup Deal",
    expiresIn: "25 days",
    saved: 1260,
    features: ["Tech Park", "Innovation Hub", "Startup Community"]
  }
];

export function MediaOwnerDeals({ ownerId }: MediaOwnerDealsProps) {
  const [deals, setDeals] = useState(mockDeals);
  const [filteredDeals, setFilteredDeals] = useState(mockDeals);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDealType, setSelectedDealType] = useState('all');
  const [sortBy, setSortBy] = useState('discount');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  
  const { addToCart, cartItems } = useCart();

  useEffect(() => {
    let filtered = deals;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(deal =>
        deal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(deal => deal.type.toLowerCase() === selectedType);
    }

    // Filter by deal type
    if (selectedDealType !== 'all') {
      filtered = filtered.filter(deal => deal.dealType.toLowerCase().includes(selectedDealType.toLowerCase()));
    }

    // Sort deals
    switch (sortBy) {
      case 'discount':
        filtered = filtered.sort((a, b) => b.discountPercent - a.discountPercent);
        break;
      case 'savings':
        filtered = filtered.sort((a, b) => b.saved - a.saved);
        break;
      case 'expiry':
        filtered = filtered.sort((a, b) => parseInt(a.expiresIn) - parseInt(b.expiresIn));
        break;
      case 'price-low':
        filtered = filtered.sort((a, b) => a.discountedPrice - b.discountedPrice);
        break;
      case 'price-high':
        filtered = filtered.sort((a, b) => b.discountedPrice - a.discountedPrice);
        break;
    }

    setFilteredDeals(filtered);
    setCurrentPage(1);
  }, [searchQuery, selectedType, selectedDealType, sortBy, deals, itemsPerPage]);

  const handleAddToCart = (deal: any) => {
    addToCart({
      id: deal.id,
      name: deal.title,
      price: deal.discountedPrice,
      originalPrice: deal.originalPrice,
      location: deal.location,
      image: deal.image,
      type: deal.type,
      size: deal.size,
      impressions: deal.impressions,
      discountPercent: deal.discountPercent
    });
  };

  const isInCart = (dealId: number) => {
    return cartItems.some(item => item.id === dealId);
  };

  // Pagination
  const totalPages = Math.ceil(filteredDeals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDeals = filteredDeals.slice(startIndex, endIndex);

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'digital': return 'bg-blue-100 text-blue-700';
      case 'classic': return 'bg-green-100 text-green-700';
      case 'package': return 'bg-purple-100 text-purple-700';
      case 'transit': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDealTypeColor = (dealType: string) => {
    switch (dealType.toLowerCase()) {
      case 'flash sale': return 'bg-red-500 text-white';
      case 'limited time': return 'bg-orange-500 text-white';
      case 'clearance': return 'bg-purple-500 text-white';
      case 'bundle offer': return 'bg-blue-500 text-white';
      case 'early bird': return 'bg-green-500 text-white';
      case 'weekend special': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getUrgencyColor = (expiresIn: string) => {
    const days = parseInt(expiresIn);
    if (days <= 2) return 'text-red-600';
    if (days <= 5) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      {/* Deals Header */}
      <Card className="bg-gradient-to-r from-emerald-50 to-blue-50">
        <CardContent className="p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              🔥 Special Deals & Offers
            </h2>
            <p className="text-gray-600">
              Save big on premium  inventory with our exclusive deals
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search deals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full lg:w-auto">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="digital">Digital</SelectItem>
                  <SelectItem value="classic">Classic</SelectItem>
                  <SelectItem value="package">Package</SelectItem>
                  <SelectItem value="transit">Transit</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedDealType} onValueChange={setSelectedDealType}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="All Deal Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Deal Types</SelectItem>
                  <SelectItem value="flash">Flash Sale</SelectItem>
                  <SelectItem value="limited">Limited Time</SelectItem>
                  <SelectItem value="clearance">Clearance</SelectItem>
                  <SelectItem value="bundle">Bundle Offer</SelectItem>
                  <SelectItem value="early">Early Bird</SelectItem>
                  <SelectItem value="weekend">Weekend Special</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount">Highest Discount</SelectItem>
                  <SelectItem value="savings">Highest Savings</SelectItem>
                  <SelectItem value="expiry">Expiring Soon</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
                <SelectTrigger className="w-full sm:w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="9">9</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="18">18</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Results Count */}
          <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row gap-2 sm:items-center justify-between text-sm text-gray-600">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <span>
                {filteredDeals.length} {filteredDeals.length === 1 ? 'deal' : 'deals'} available
              </span>
              <span>
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredDeals.length)} of {filteredDeals.length}
              </span>
            </div>
            <span className="text-emerald-600 font-medium">
              Total potential savings: ${filteredDeals.reduce((sum, deal) => sum + deal.saved, 0)?.toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Deals Grid */}
      {currentDeals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-gray-400 mb-4">
              <Tag className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No deals found</h3>
            <p className="text-gray-600">Try adjusting your search terms or filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentDeals.map((deal) => (
            <Card key={deal.id} className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-emerald-200">
              <div className="relative">
                <img
                  src={deal.image}
                  alt={deal.title}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
                
                {/* Deal Type Badge */}
                <Badge 
                  className={`absolute top-3 left-3 ${getDealTypeColor(deal.dealType)} font-semibold`}
                >
                  {deal.dealType}
                </Badge>
                
                {/* Discount Badge */}
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm">
                  -{deal.discountPercent}%
                </div>
                
                {/* Type Badge */}
                <Badge 
                  className={`absolute bottom-3 left-3 ${getTypeColor(deal.type)}`}
                >
                  {deal.type}
                </Badge>
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-emerald-600 transition-colors">
                  {deal.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-3 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {deal.location}
                </p>
                
                {/* Pricing */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl font-bold text-emerald-600">
                      ${deal.discountedPrice?.toLocaleString()}
                    </span>
                    <span className="text-lg text-gray-400 line-through">
                      ${deal.originalPrice?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-600 font-semibold">
                      You save ${deal.saved?.toLocaleString()}
                    </span>
                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                      <Percent className="h-3 w-3 mr-1" />
                      {deal.discountPercent}% OFF
                    </Badge>
                  </div>
                </div>
                
                {/* Deal Info */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    {deal.size}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    {deal.impressions?.toLocaleString()} impressions
                  </span>
                </div>
                
                {/* Expiry Warning */}
                <div className={`flex items-center gap-1 mb-4 text-sm font-medium ${getUrgencyColor(deal.expiresIn)}`}>
                  <Clock className="h-4 w-4" />
                  <span>Expires in {deal.expiresIn}</span>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => window.location.href = `/billboard/${deal.id}?from=store&ownerId=${ownerId}`}>
                    <Eye className="h-4 w-4 mr-2" />
                    Details
                  </Button>
                  <Button 
                    size="sm" 
                    className={`flex-1 ${
                      isInCart(deal.id) 
                        ? 'bg-gray-400 hover:bg-gray-400' 
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                    onClick={() => handleAddToCart(deal)}
                    disabled={isInCart(deal.id)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {isInCart(deal.id) ? 'In Cart' : 'Add to Cart'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Enhanced Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-6">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex space-x-1">
              {/* First page */}
              {currentPage > 3 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    className="w-8 h-8 p-0"
                  >
                    1
                  </Button>
                  {currentPage > 4 && (
                    <span className="px-2 py-1 text-gray-500">...</span>
                  )}
                </>
              )}
              
              {/* Page numbers around current page */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                const page = startPage + i;
                
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 p-0 ${
                        currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700' : ''
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
              
              {/* Last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 py-1 text-gray-500">...</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    className="w-8 h-8 p-0"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <div className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </div>
  );
}